/* =============================================================
   DocumentContext.js — Document upload and review state
   -------------------------------------------------------------
   Manages the full document lifecycle connected to Supabase:

     Student uploads file
       → stored in Supabase Storage bucket "documents"
       → metadata row inserted into the documents table
       → review_status = 'pending'

     IO manager opens student profile
       → documents fetched from DB for that student
       → can Approve (review_status → 'approved')
       → can Reject  (review_status → 'rejected')

   Cache strategy:
     Each student's documents are loaded once per session and
     cached in uploadsCache[studentId]. A loadedStudentsRef
     prevents duplicate fetches without adding cache to deps.

   Global pending count:
     pendingCount is loaded on login and kept in sync with
     every approve/reject/upload action — used by the Sidebar
     badge and IncomingList without an extra query.

   ✓ DONE (backend integration):
   - Replaced localStorage + base64 with Supabase Storage
   - uploadDocument()   → storage.upload + documents.insert
   - approveDocument()  → documents.update (review_status)
   - rejectDocument()   → documents.update (review_status)
   - viewDocument()     → storage.createSignedUrl (60 min)
   - getPendingCount()  → live count from DB on login
   ============================================================= */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth }  from './AuthContext';


/* ─── CONTEXT ────────────────────────────────────────────────── */
const DocumentContext = createContext();


/* ─── ROW → UI SHAPE ─────────────────────────────────────────── */
// Maps a documents table row to the shape the UI expects.
function toUIDoc(row) {
  return {
    id:          row.id,
    studentId:   row.student_id,
    docName:     row.doc_name,
    fileName:    row.original_file_name,
    fileType:    row.file_type,
    storagePath: row.storage_path,
    status:      row.review_status,
    uploadedAt:  row.uploaded_at,
  };
}


/* ─── DOCUMENT PROVIDER ──────────────────────────────────────── */
export function DocumentProvider({ children }) {
  const { user } = useAuth();

  // Per-student document cache: { [studentId]: UIDoc[] }
  const [uploadsCache,    setUploadsCache]    = useState({});

  // Set of studentIds currently being fetched (for loading spinners)
  const [loadingStudents, setLoadingStudents] = useState(new Set());

  // Per-type pending counts for the Sidebar badges
  const [pendingIncoming, setPendingIncoming] = useState(0);
  const [pendingOutgoing, setPendingOutgoing] = useState(0);

  // Tracks which students have already been loaded (avoids duplicate fetches
  // without making loadUploadsForStudent depend on uploadsCache).
  const loadedRef = useRef(new Set());


  /* ── Load pending counts split by student type ─────────────── */
  // Joins documents → students to separate incoming vs outgoing.
  // Called on login and after every upload/approve/reject action.
  const loadCounts = useCallback(async () => {
    if (!user?.universityId) return;
    const { data } = await supabase
      .from('documents')
      .select('id, students!inner(type)')
      .eq('university_id', user.universityId)
      .eq('review_status', 'pending');
    const rows = data ?? [];
    setPendingIncoming(rows.filter(d => d.students?.type === 'incoming').length);
    setPendingOutgoing(rows.filter(d => d.students?.type === 'outgoing').length);
  }, [user?.universityId]);

  useEffect(() => { loadCounts(); }, [loadCounts]);


  /* ── loadUploadsForStudent ──────────────────────────────────── */
  // Fetches all documents for a student from Supabase and caches them.
  // Safe to call multiple times — subsequent calls for the same student
  // are ignored (loadedRef prevents duplicate fetches).
  const loadUploadsForStudent = useCallback(async (studentId) => {
    if (!studentId || loadedRef.current.has(studentId)) return;
    loadedRef.current.add(studentId);

    setLoadingStudents(prev => new Set([...prev, studentId]));

    const { data } = await supabase
      .from('documents')
      .select('*')
      .eq('student_id', studentId)
      .order('uploaded_at', { ascending: false });

    setUploadsCache(prev => ({
      ...prev,
      [studentId]: (data ?? []).map(toUIDoc),
    }));

    setLoadingStudents(prev => {
      const next = new Set([...prev]);
      next.delete(studentId);
      return next;
    });
  }, []); // Stable — no deps that change


  /* ── uploadDocument ─────────────────────────────────────────── */
  // 1. Cancella dal DB e dallo Storage il vecchio file (se esiste)
  //    per lo stesso studente + nome documento. Questo gestisce il
  //    caso in cui lo studente fa re-upload dopo un rifiuto.
  // 2. Carica il nuovo file su Supabase Storage.
  // 3. Inserisce la nuova riga nella tabella documents.
  // 4. Aggiorna la cache locale in modo ottimistico.
  // 5. Incrementa il contatore globale dei pending.
  //
  // Storage path: {university_id}/{student_id}/{uuid}.{ext}
  const uploadDocument = useCallback(async (studentId, docName, file) => {
    if (!user?.universityId) throw new Error('Not authenticated');

    // 1. Cerca e cancella eventuali versioni precedenti dello stesso documento
    //    (stesso studente + stesso nome doc). Questo evita che i vecchi file
    //    rifiutati rimangano in Storage senza essere usati.
    const { data: existing } = await supabase
      .from('documents')
      .select('id, storage_path, review_status')
      .eq('student_id', studentId)
      .eq('doc_name',   docName);

    if (existing?.length) {
      const oldPaths = existing.map(d => d.storage_path);
      const oldIds   = existing.map(d => d.id);

      // Cancella i file dallo Storage e le righe dal DB in parallelo.
      // Gli errori di cleanup non bloccano l'upload del nuovo file.
      await Promise.allSettled([
        supabase.storage.from('documents').remove(oldPaths),
        supabase.from('documents').delete().in('id', oldIds),
      ]);

      // (il contatore globale viene ricalcolato alla fine via loadCounts)
    }

    // 2. Carica il nuovo file su Storage
    const ext         = file.name.includes('.') ? file.name.split('.').pop() : 'bin';
    const storagePath = `${user.universityId}/${studentId}/${crypto.randomUUID()}.${ext}`;

    const { error: storageError } = await supabase.storage
      .from('documents')
      .upload(storagePath, file, { contentType: file.type, upsert: false });

    if (storageError) throw storageError;

    // 3. Inserisce la nuova riga nella tabella documents
    const { data: row, error: dbError } = await supabase
      .from('documents')
      .insert({
        university_id:      user.universityId,
        student_id:         studentId,
        doc_name:           docName,
        original_file_name: file.name,
        file_type:          file.type,
        storage_path:       storagePath,
        review_status:      'pending',
      })
      .select()
      .single();

    if (dbError) {
      // Pulizia del file su Storage se l'insert nel DB fallisce
      await supabase.storage.from('documents').remove([storagePath]);
      throw dbError;
    }

    const newDoc = toUIDoc(row);

    // 4. Aggiorna la cache — sostituisce qualsiasi doc con lo stesso nome
    setUploadsCache(prev => ({
      ...prev,
      [studentId]: [
        ...(prev[studentId] ?? []).filter(d => d.docName !== docName),
        newDoc,
      ],
    }));

    // 5. Ricalcola i contatori globali (incoming vs outgoing)
    loadCounts();
  }, [user?.universityId, loadCounts]);


  /* ── findDocById ────────────────────────────────────────────── */
  // Synchronous lookup across the full cache — used internally by
  // approveDocument and rejectDocument to find the studentId.
  const findDocById = useCallback((id) => {
    for (const docs of Object.values(uploadsCache)) {
      const found = docs.find(d => d.id === id);
      if (found) return found;
    }
    return null;
  }, [uploadsCache]);


  /* ── approveDocument ────────────────────────────────────────── */
  const approveDocument = useCallback(async (id) => {
    const { error } = await supabase
      .from('documents')
      .update({
        review_status: 'approved',
        reviewed_by:   user?.id,
        reviewed_at:   new Date().toISOString(),
      })
      .eq('id', id);

    if (error) throw error;

    const doc = findDocById(id);
    if (doc) {
      setUploadsCache(prev => ({
        ...prev,
        [doc.studentId]: (prev[doc.studentId] ?? []).map(d =>
          d.id === id ? { ...d, status: 'approved' } : d
        ),
      }));
    }
    loadCounts();
  }, [findDocById, loadCounts, user?.id]);


  /* ── rejectDocument ─────────────────────────────────────────── */
  const rejectDocument = useCallback(async (id) => {
    const { error } = await supabase
      .from('documents')
      .update({
        review_status: 'rejected',
        reviewed_by:   user?.id,
        reviewed_at:   new Date().toISOString(),
      })
      .eq('id', id);

    if (error) throw error;

    const doc = findDocById(id);
    if (doc) {
      setUploadsCache(prev => ({
        ...prev,
        [doc.studentId]: (prev[doc.studentId] ?? []).map(d =>
          d.id === id ? { ...d, status: 'rejected' } : d
        ),
      }));
    }
    loadCounts();
  }, [findDocById, loadCounts, user?.id]);


  /* ── getUpload ──────────────────────────────────────────────── */
  // Synchronous lookup from the cache.
  // Returns the most recent upload for a student + doc name, or null.
  const getUpload = useCallback((studentId, docName) =>
    (uploadsCache[studentId] ?? []).find(d => d.docName === docName) ?? null
  , [uploadsCache]);


  /* ── getPendingCount ────────────────────────────────────────── */
  // With studentId → per-student count from the local cache.
  // No studentId   → total university-wide count (incoming + outgoing).
  const getPendingCount = useCallback((studentId) => {
    if (!studentId) return pendingIncoming + pendingOutgoing;
    return (uploadsCache[studentId] ?? []).filter(d => d.status === 'pending').length;
  }, [uploadsCache, pendingIncoming, pendingOutgoing]);


  /* ── reloadStudentDocuments ─────────────────────────────────── */
  // Forces a fresh fetch from Supabase for a specific student,
  // bypassing the loadedRef cache guard.
  // Call this after approve/reject to guarantee the UI reflects
  // the latest DB state even if the local cache update was skipped.
  const reloadStudentDocuments = useCallback(async (studentId) => {
    if (!studentId) return;

    // Remove from loadedRef so loadUploadsForStudent won't skip the fetch.
    loadedRef.current.delete(studentId);

    setLoadingStudents(prev => new Set([...prev, studentId]));

    const { data } = await supabase
      .from('documents')
      .select('*')
      .eq('student_id', studentId)
      .order('uploaded_at', { ascending: false });

    setUploadsCache(prev => ({
      ...prev,
      [studentId]: (data ?? []).map(toUIDoc),
    }));

    setLoadingStudents(prev => {
      const next = new Set([...prev]);
      next.delete(studentId);
      return next;
    });

    // Re-add to loadedRef so future calls stay cached.
    loadedRef.current.add(studentId);
  }, []); // Stable — no deps that change


  /* ── Real-time subscription ──────────────────────────────────── */
  // Keeps pending badge counts and per-student document lists in sync
  // across all IO managers working on the same university simultaneously.
  // Placed after reloadStudentDocuments to avoid temporal dead zone errors.
  useEffect(() => {
    if (!user?.universityId) return;

    const channel = supabase
      .channel(`documents_${user.universityId}`)
      .on(
        'postgres_changes',
        {
          event:  '*',
          schema: 'public',
          table:  'documents',
          filter: `university_id=eq.${user.universityId}`,
        },
        (payload) => {
          loadCounts();
          const studentId = payload.new?.student_id ?? payload.old?.student_id;
          if (studentId && loadedRef.current.has(studentId)) {
            reloadStudentDocuments(studentId);
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.universityId, loadCounts, reloadStudentDocuments]);


  /* ── viewDocument ───────────────────────────────────────────── */
  // Genera un signed URL valido 60 minuti e apre il file in un nuovo tab.
  const viewDocument = useCallback(async (upload) => {
    if (!upload?.storagePath) return;

    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(upload.storagePath, 3600);

    if (error) { console.error('Could not generate signed URL:', error.message); return; }
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  }, []);


  /* ── downloadDocument ───────────────────────────────────────── */
  // Forza il download del file con il nome originale.
  //
  // Perché non basta un <a download="..."> sul signed URL?
  // I signed URL di Supabase Storage sono cross-origin: il browser
  // ignora l'attributo "download" sugli URL di origine diversa e apre
  // il file nel tab invece di scaricarlo.
  //
  // Soluzione: scarica il file come Blob tramite fetch(), crea un URL
  // locale (blob:) — che è same-origin — e triggera il download da lì.
  const downloadDocument = useCallback(async (upload) => {
    if (!upload?.storagePath) return;

    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(upload.storagePath, 3600);

    if (error) { console.error('Could not generate signed URL:', error.message); return; }
    if (!data?.signedUrl) return;

    try {
      const response  = await fetch(data.signedUrl);
      const blob      = await response.blob();
      const blobUrl   = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href     = blobUrl;
      a.download = upload.fileName ?? 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Libera la memoria dopo che il download è partito
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, []);


  return (
    <DocumentContext.Provider value={{
      uploadsCache,
      pendingIncoming,
      pendingOutgoing,
      loadingStudents,
      loadUploadsForStudent,
      reloadStudentDocuments,
      uploadDocument,
      approveDocument,
      rejectDocument,
      getUpload,
      getPendingCount,
      viewDocument,
      downloadDocument,
    }}>
      {children}
    </DocumentContext.Provider>
  );
}


/* ─── useDocuments ───────────────────────────────────────────── */
// General hook — use this in Sidebar, IncomingList, etc.
// Provides getPendingCount() for the global badge.
export const useDocuments = () => useContext(DocumentContext);


/* ─── useStudentDocuments ────────────────────────────────────── */
// Per-student hook for profile pages.
// Automatically loads documents for the given student on mount.
//
// Usage:
//   const { documents, docsLoading, uploadDocument, ... } = useStudentDocuments(studentId);
//
// Returns:
//   documents       — array of UIDoc for this student (from cache)
//   docsLoading     — true while fetching from Supabase
//   uploadDocument  — (docName, file) => Promise  (studentId pre-filled)
//   approveDocument — (id) => Promise
//   rejectDocument  — (id) => Promise
//   viewDocument    — (upload) => void
export function useStudentDocuments(studentId) {
  const ctx = useDocuments();

  useEffect(() => {
    if (studentId) ctx.loadUploadsForStudent(studentId);
  }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    documents:               ctx.uploadsCache[studentId] ?? [],
    docsLoading:             ctx.loadingStudents.has(studentId),
    uploadDocument:          (docName, file) => ctx.uploadDocument(studentId, docName, file),
    approveDocument:         ctx.approveDocument,
    rejectDocument:          ctx.rejectDocument,
    viewDocument:            ctx.viewDocument,
    downloadDocument:        ctx.downloadDocument,
    reloadStudentDocuments:  () => ctx.reloadStudentDocuments(studentId),
  };
}
